import { useAppSelector } from 'src/store/store';
import { selectTenantFeatures, selectAuthenticatedConfigLoaded, selectAuthenticatedConfigFailed, TenantFeatures } from 'src/reducers/tenantConfigSlice';

/**
 * Hook to access tenant feature flags.
 * Drop-in replacement for the old `getTenantFeatures()`.
 *
 * Public features (login UI) are available after login page loads.
 * Authenticated features (post-login) are available after login.
 * Safe defaults are used while configs are loading.
 *
 * `isConfigLoaded` is true once the authenticated config fetch has settled (see `isConfigFailed`).
 * Components that make one-time decisions (useEffect with [] deps) MUST wait for
 * isConfigLoaded before acting, otherwise they'll use default values.
 */
export const useTenantFeatures = (): TenantFeatures & { isConfigLoaded: boolean; isConfigFailed: boolean } => {
  const features = useAppSelector(selectTenantFeatures);
  const isConfigLoaded = useAppSelector(selectAuthenticatedConfigLoaded);
  // True when the fetch settled WITHOUT a config (isConfigLoaded is true then too, with the
  // default empty enabledModules): callers that gate on enabledModules can tell "no modules" from
  // "unknown".
  const isConfigFailed = useAppSelector(selectAuthenticatedConfigFailed);
  return { ...features, isConfigLoaded, isConfigFailed };
};

/**
 * Check if a domain is configured for auto-login (Google OAuth).
 * Replacement for the old `isAutoLoginDomain()`.
 */
export const useIsAutoLoginDomain = (): ((email: string) => boolean) => {
  const features = useAppSelector(selectTenantFeatures);
  return (email: string): boolean => {
    const domain = email.split('@')[1]?.toLowerCase();
    return domain ? features.autoLoginDomains.includes(domain) : false;
  };
};
